import { Signal, computed, type ReadonlySignal } from '@preact/signals';
import memorise from 'lru-memorise';
import type { Product } from '@/domain/Product';
import {
  filterableAttributes,
  sortableAttributes,
  type ConfigUniqueId,
  type Filter,
  type FilterableAttributeKey,
  type FilterableValue,
  type FilterOption,
  type SortOption
} from '@/domain/FilteringAndSorting.ts';
import type { I18NInstance } from '@/utils/createI18nInstance';
import type { ProductTranslations } from '@/domain/config';

export type Filtering = {
  filters: ReadonlySignal<Filter[]>;
  currentFilters: ReadonlySignal<
    [filter: Filter, filterOption: FilterOption][]
  >;
  isFiltered: ReadonlySignal<boolean>;
  isFilteredBy: (id: Filter['id']) => ReadonlySignal<boolean>;
  updateFilter: (
    id: Filter['id'],
    option: FilterOption['id'],
    selected: boolean
  ) => void;
  clearAllFilters: () => void;
  isDisabledFilterOption: (
    id: Filter['id'],
    option: FilterOption['id']
  ) => ReadonlySignal<boolean>;
};

export type Sorting = {
  sortOptions: ReadonlySignal<SortOption[]>;
  currentSort: ReadonlySignal<SortOption | undefined>;
  isSorted: ReadonlySignal<boolean>;
  applySort: (id: SortOption['id']) => void;
  clearSort: () => void;
};

export type FiltersAndSort = {
  products: ReadonlySignal<Product[]>;
  productsWithFilter: (
    id: Filter['id'],
    option: FilterOption['id']
  ) => ReadonlySignal<Product[]>;
  filtering: Filtering;
  sorting: Sorting;
};

const FILTER_METHOD_OR = 'OR';

export function initFiltersAndSort(
  products: Product[],
  initFilters: Filter[],
  initSortOptions: SortOption[],
  productI18nInstance: I18NInstance<ProductTranslations>,
  initSelectedFilters?: `${ConfigUniqueId}:${ConfigUniqueId}`[],
  initSelectedSort?: ConfigUniqueId
): FiltersAndSort {
  const { language: locale } = productI18nInstance;

  const selectedFilters = new Signal<[Filter, FilterOption][]>(
    createInitSelectedFilters(initFilters, initSelectedFilters)
  );

  type SelectedFilterValues = {
    key: FilterableAttributeKey;
    values: FilterableValue[];
  };

  const selectedFilterValuesByIdMap = computed(() => {
    const result = new Map<ConfigUniqueId, SelectedFilterValues>();
    for (const [filter, filterOption] of selectedFilters.value) {
      const isORCondition = filter.method === FILTER_METHOD_OR;
      const id = isORCondition
        ? (getIdToCombine(initFilters, filter) ?? filter.id)
        : filter.id;

      if (!result.has(id)) {
        result.set(id, { key: filter.key, values: [] });
      }

      const selections = result.get(id)!;
      result.set(id, {
        key: selections.key,
        values: selections.values.concat(filterOption.values)
      });
    }

    return result;
  });

  const selectedValuesByFilterKeyMap = computed(() => {
    const result = new Map<FilterableAttributeKey, FilterableValue[]>();
    for (const [filter, filterOption] of selectedFilters.value) {
      if (!result.has(filter.key)) {
        result.set(filter.key, []);
      }

      const values = result.get(filter.key)!;
      result.set(filter.key, values.concat(filterOption.values));
    }

    return result;
  });

  const filters = computed(() =>
    initFilters.map((filter) => ({
      ...filter,
      options: [filter.options].flat().map((option) => {
        const selectedValues =
          selectedValuesByFilterKeyMap.value.get(filter.key) ?? [];
        return {
          ...option,
          selected: selectedValues.some((value) =>
            option.values.includes(value)
          )
        };
      })
    }))
  );

  const sortOptions = new Signal<SortOption[]>(initSortOptions);

  const currentSort = new Signal<SortOption | undefined>(
    createInitCurrentSort(initSortOptions, initSelectedSort)
  );

  function filterProducts(explicitFilter?: {
    id: Filter['id'];
    option: FilterOption['id'];
    parentCategoryName?: string;
  }): ReadonlySignal<Product[]> {
    const userSelectedFilterValuesById = selectedFilterValuesByIdMap.value;
    const mergedSelectedFilterValuesById = new Map(
      userSelectedFilterValuesById
    );

    if (explicitFilter) {
      const filter = getFilterById(filters.value, explicitFilter.id);

      if (filter) {
        const explicitSelectedFilter = {
          id: filter.id,
          key: filter.key,
          values: [filter.options]
            .flat()
            .filter((option) => option.id === explicitFilter.option)
            .flatMap((option) => option.values)
        };

        mergedSelectedFilterValuesById.set(explicitSelectedFilter.id, {
          key: explicitSelectedFilter.key,
          values: explicitSelectedFilter.values
        });
      }
    }

    return computed(() =>
      products.filter((product) => {
        const parentCategoryName = explicitFilter?.parentCategoryName;

        const filterable = filterableAttributes(
          product,
          productI18nInstance,
          parentCategoryName
        );
        for (const [, selections] of mergedSelectedFilterValuesById) {
          const attributeValues = filterable[selections.key];

          let match = false; // Default to filter out if attribute does not exist
          for (const filterValue of selections.values) {
            if (attributeValues.has(filterValue)) {
              match = true; // Found a match, so don't filter out
              break;
            }
          }

          if (!match) return false;
        }
        return true; // No Filters, so no filtering
      })
    );
  }

  function sortedProducts(
    availableProducts: ReadonlySignal<Product[]>
  ): Product[] {
    const sort = currentSort.value;

    const inStockProducts = availableProducts.value.filter(
      (product) => product.inStock
    );
    const outOfStockProducts = availableProducts.value.filter(
      (product) => !product.inStock
    );

    if (!sort)
      return [
        ...inStockProducts.sort(defaultSortComparator),
        ...outOfStockProducts.sort(defaultSortComparator)
      ];

    const sortFunc = getSortComparator(sort, locale);
    const combinedComparator = (productA: Product, productB: Product) =>
      sortFunc(productA, productB) || defaultSortComparator(productA, productB);

    let productsToOutput = [
      ...inStockProducts.sort(combinedComparator),
      ...outOfStockProducts.sort(combinedComparator)
    ];

    if (sort?.key === 'intensity') {
      const groupedProducts = Object.groupBy(productsToOutput, (product) =>
        product.type === 'accessory' ? 'accessory' : 'nonAccessory'
      );

      if (sort?.order === 'ASCENDING') {
        productsToOutput = [
          ...(groupedProducts.accessory || []),
          ...(groupedProducts.nonAccessory || [])
        ];
      } else {
        productsToOutput = [
          ...(groupedProducts.nonAccessory || []),
          ...(groupedProducts.accessory || [])
        ];
      }
    }

    return productsToOutput;
  }

  return {
    products: computed(() => {
      const filteredProducts = filterProducts();

      return sortedProducts(filteredProducts);
    }),

    productsWithFilter(id: Filter['id'], option: FilterOption['id']) {
      return filterProducts({ id, option });
    },

    filtering: {
      filters,

      currentFilters: selectedFilters,

      isFiltered: computed(() =>
        filters.value.some((filter) =>
          [filter.options].flat().some((option) => option.selected)
        )
      ),

      isFilteredBy: (filterId) =>
        computed(() => {
          const filter = getFilterById(filters.value, filterId);
          if (!filter) return false;

          return filter.options.some((option) => option.selected);
        }),

      updateFilter(id, optionId, selected) {
        const filter = getFilterById(filters.value, id);
        if (!filter) return;

        const filterOption = getFilterOptionById(filter, optionId);
        if (!filterOption) return;

        if (selected) {
          selectedFilters.value = [
            ...selectedFilters.peek(),
            [filter, filterOption]
          ];
        } else {
          selectedFilters.value = selectedFilters.peek().filter(
            ([filterItem, filterOptionItem]) =>
              !(
                // Remove the selected filter option
                (
                  (filterItem.id === filter.id &&
                    filterOptionItem.id === filterOption.id) ||
                  // Also remove other selected filter options with the same key and a matching value
                  (filterItem.key === filter.key &&
                    filterOptionItem.values.some((value) =>
                      filterOption.values.includes(value)
                    ))
                )
              )
          );
        }
      },

      clearAllFilters() {
        selectedFilters.value = [];
      },

      isDisabledFilterOption: (id, option) =>
        computed(() => {
          const filter = getFilterById(filters.value, id);

          if (!filter) return false;

          const filterOption = getFilterOptionById(filter, option);

          if (!filterOption || filterOption.selected) return false;

          // If the filter is an OR condition and there is already a selected filter with the same key,
          // we allow the option to be selected.
          if (
            filter.method === FILTER_METHOD_OR &&
            selectedFilters.value.some(
              ([selectedFilter]) =>
                selectedFilter.method === FILTER_METHOD_OR &&
                selectedFilter.key === filter.key
            )
          ) {
            return false;
          }

          // An Explicit Filter option is applied to the current filters, to see if the
          // option should be disabled. i.e. resulting product list would have 0 length
          const parentCategoryName = filter?.parentCategoryName;
          return (
            filterProducts({ id, option, parentCategoryName }).value.length ===
            0
          );
        })
    },

    sorting: {
      sortOptions,

      currentSort,

      isSorted: computed(() => currentSort.value !== undefined),

      applySort(id) {
        const match = sortOptions.value.find((option) => option.id === id);
        if (match) {
          currentSort.value = match;
        }
      },

      clearSort() {
        currentSort.value = undefined;
      }
    }
  };
}

function getIdToCombine(filters: Filter[], { key, id }: Filter) {
  return (
    filters.find((filter) => filter.id !== id && filter.key === key)?.id ?? null
  );
}

function getFilterById(
  filters: Filter[],
  id: Filter['id']
): Filter | undefined {
  return findWithIndexById<Filter>(
    'filters', // Note: Assumes filters does not mutate order over time
    filters,
    id
  );
}

function getFilterOptionById(
  filter: Filter,
  id: FilterOption['id']
): FilterOption | undefined {
  return findWithIndexById<FilterOption>(
    filter.id, // Note: Assumes filter's options and their values do not mutate order over time
    [filter?.options].flat(),
    id
  );
}

function findWithIndexById<T extends { id?: string; value?: { id?: string } }>(
  cacheId: string,
  collection: T[],
  id: string
): T | undefined {
  const index = getIndex(cacheId);

  if (!index.has(id)) {
    const itemIndex = collection.findIndex(
      (item) => item?.id === id || item?.value?.id === id
    );
    index.set(id, itemIndex);
  }

  const itemIndex = index.get(id)!;
  if (itemIndex === -1) {
    return undefined;
  }

  return collection[itemIndex];
}

const getIndex = memorise((_cacheId) => new Map<string, number>(), {
  cacheKeyResolver: (cacheId) => cacheId
});

const getSortComparator = memorise(
  (sort: SortOption, locale: string) => {
    const { key, order } = sort;

    const compare = (productA: Product, productB: Product) => {
      const sortableA = sortableAttributes(productA);
      const sortableB = sortableAttributes(productB);

      const valueA = sortableA[key];
      const valueB = sortableB[key];

      // Note: If the sort property is not defined, it will always go at the end
      if (valueA === undefined && valueB === undefined) return 0;
      if (valueA === undefined) return 1;
      if (valueB === undefined) return -1;

      if (sort.type === 'NUMERIC') {
        return order === 'ASCENDING'
          ? Number(valueA) - Number(valueB)
          : Number(valueB) - Number(valueA);
      }

      return order === 'ASCENDING'
        ? String(valueA).localeCompare(String(valueB), locale)
        : String(valueB).localeCompare(String(valueA), locale);
    };

    return (productA: Product, productB: Product) => {
      const comparatorValue = compare(productA, productB);

      // Push bundles downward when values are identical
      if (comparatorValue === 0) {
        if (productA.bundle && !productB.bundle) {
          return 1;
        }
        if (!productA.bundle && productB.bundle) {
          return -1;
        }
      }

      return comparatorValue;
    };
  },
  { cacheKeyResolver: (sort) => `${sort.key}-${sort.order}` }
);

function defaultSortComparator(productA: Product, productB: Product): number {
  return (
    (productA.metadata?.listPosition ?? 0) -
    (productB.metadata?.listPosition ?? 0)
  );
}

function createInitSelectedFilters(
  filters: Filter[],
  initSelectedFilters?: `${ConfigUniqueId}:${ConfigUniqueId}`[]
): [Filter, FilterOption][] {
  if (!initSelectedFilters) return [];

  return [initSelectedFilters]
    .flat()
    .map((selection) => {
      const [filterId, filterOptionId] = selection.split(':');
      const filter = getFilterById(filters, filterId);
      if (!filter) return undefined;

      const filterOption = getFilterOptionById(filter, filterOptionId);
      if (!filterOption) return undefined;

      return [filter, filterOption];
    })
    .filter((selection) => selection !== undefined) as [Filter, FilterOption][];
}

function createInitCurrentSort(
  sortOptions: SortOption[],
  initSelectedSort?: ConfigUniqueId
): SortOption | undefined {
  if (!initSelectedSort) return undefined;

  return sortOptions.find((option) => option.id === initSelectedSort);
}
